import { useState, useMemo, useEffect, Fragment } from 'react';
import { DatePickerInput } from './DatePickerInput';
import * as XLSX from 'xlsx';
import { useConnector } from '../contexts/ConnectorContext';
import type { TimeEntry, Person, Project, ISODateString, BankHoliday } from '../types/timesheet.types';
import { ProjectType } from '../types/timesheet.types';
import { toISODateString } from '../types/timesheet.types';
import { fmtHours } from '../utils/calculations';
import type { CostTypeItem } from '../types/connector.types';

// ─── Types ────────────────────────────────────────────────────────────────────

type ReportTab = 'monthly' | 'rd' | 'project';
type PeriodKey = 'month' | 'fy' | 'by' | 'ytd' | 'custom';

interface PersonRow {
  personId: string;
  personEmail: string;
  name: string;
  dept: string;
  rd: number;
  ga: number;
  leave: number;
  total: number;
  cost: number;
}

interface ProjectRow {
  code: string;
  projectName: string;
  type: string;
  owner: string;
  hrs: number;
  cost: number;
}

interface ProjectPersonRow {
  personId: string;
  name: string;
  dept: string;
  hrs: number;
  cost: number;
}

interface PeriodRange {
  from: ISODateString;
  to: ISODateString;
  label: string;
}

// ─── Period helpers ───────────────────────────────────────────────────────────

function computePeriodRange(
  period: PeriodKey,
  customFrom = '',
  customTo = '',
): PeriodRange | null {
  if (period === 'custom' && (!customFrom || !customTo)) return null;

  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();

  try {
    if (period === 'month') {
      const mm = String(month + 1).padStart(2, '0');
      const dd = String(new Date(year, month + 1, 0).getDate()).padStart(2, '0');
      return {
        from: toISODateString(`${year}-${mm}-01`),
        to: toISODateString(`${year}-${mm}-${dd}`),
        label: now.toLocaleString('en-GB', { month: 'long', year: 'numeric' }),
      };
    }
    if (period === 'fy') {
      const sy = month >= 6 ? year : year - 1;
      const ey = sy + 1;
      return {
        from: toISODateString(`${sy}-07-01`),
        to: toISODateString(`${ey}-06-30`),
        label: `FY ${sy}\u2013${String(ey).slice(2)} (Jul ${sy} \u2013 Jun ${ey})`,
      };
    }
    if (period === 'by') {
      return {
        from: toISODateString(`${year}-01-01`),
        to: toISODateString(`${year}-12-31`),
        label: `BY ${year} (Jan \u2013 Dec)`,
      };
    }
    if (period === 'ytd') {
      const today = now.toISOString().slice(0, 10);
      const mAbbr = now.toLocaleString('en-GB', { month: 'short' });
      return {
        from: toISODateString(`${year}-01-01`),
        to: toISODateString(today),
        label: `YTD ${mAbbr} ${year}`,
      };
    }
    // custom
    return {
      from: toISODateString(customFrom),
      to: toISODateString(customTo),
      label: 'Custom range',
    };
  } catch {
    return null;
  }
}

// ─── Aggregation ──────────────────────────────────────────────────────────────

function buildPersonRows(
  entries: TimeEntry[],
  people: Person[],
  projects: Project[],
  costTypes: CostTypeItem[],
  bhDatesInRange: Map<string, number> = new Map(),
): PersonRow[] {
  const projTypeMap = new Map(projects.map(p => [p.projectCode, p.type]));
  const peopleMap = new Map(people.map(p => [p.m365Email.toLowerCase(), p]));
  const costRateMap = new Map(costTypes.map(ct => [ct.name, ct.costRate]));
  const bhDateList = Array.from(bhDatesInRange.entries()); // [date, defaultHours][]

  const grouped = new Map<string, TimeEntry[]>();
  for (const e of entries) {
    const key = e.personEmail.toLowerCase();
    const arr = grouped.get(key) ?? [];
    arr.push(e);
    grouped.set(key, arr);
  }

  const rows: PersonRow[] = [];
  for (const [pEmail, pEntries] of grouped) {
    const first = pEntries[0];
    const person = peopleMap.get(pEmail);
    const rate = costRateMap.get(person?.costType ?? '') ?? 0;
    let rd = 0, ga = 0, leave = 0, total = 0;
    for (const e of pEntries) {
      const pType = projTypeMap.get(e.projectCode) ?? ProjectType.Standard;
      total += e.hours;
      if (pType === ProjectType.RD) rd += e.hours;
      else if (pType === ProjectType.GA) ga += e.hours;
      else if (pType === ProjectType.Leave) leave += e.hours;
    }
    // Only count BH days this person did NOT submit project hours for
    const workedDates = new Set<string>(pEntries.map(e => e.dateWorked));
    const personBHHours = bhDateList
      .filter(([d]) => !workedDates.has(d))
      .reduce((s, [, h]) => s + h, 0);
    const leaveWithBH = leave + personBHHours;
    const totalWithBH = total + personBHHours;
    rows.push({
      personId: person?.id ?? pEmail,
      personEmail: pEmail,
      name: person?.fullName ?? first.personName ?? pEmail,
      dept: person?.department ?? '—',
      rd,
      ga,
      leave: leaveWithBH,
      total: totalWithBH,
      cost: Math.round(totalWithBH * rate),
    });
  }
  return rows.sort((a, b) => b.total - a.total);
}

function buildProjectRows(
  entries: TimeEntry[],
  projects: Project[],
  people: Person[],
  costTypes: CostTypeItem[],
  bhDatesInRange: Map<string, number> = new Map(),
): ProjectRow[] {
  const projMap = new Map(projects.map(p => [p.projectCode, p]));
  const peopleMap = new Map(people.map(p => [p.m365Email.toLowerCase(), p]));
  const costRateMap = new Map(costTypes.map(ct => [ct.name, ct.costRate]));
  const bhDateList = Array.from(bhDatesInRange.entries()); // [date, defaultHours][]

  const grouped = new Map<string, TimeEntry[]>();
  for (const e of entries) {
    const arr = grouped.get(e.projectCode) ?? [];
    arr.push(e);
    grouped.set(e.projectCode, arr);
  }

  const rows: ProjectRow[] = [];
  for (const [code, cEntries] of grouped) {
    const proj = projMap.get(code);
    let hrs = 0, cost = 0;
    for (const e of cEntries) {
      hrs += e.hours;
      const person = peopleMap.get(e.personEmail.toLowerCase());
      const rate = costRateMap.get(person?.costType ?? '') ?? 0;
      cost += e.hours * rate;
    }
    rows.push({
      code,
      projectName: proj?.projectName ?? '',
      type: proj?.type ?? ProjectType.Standard,
      owner: proj?.projectOwnerName ?? '—',
      hrs,
      cost: Math.round(cost),
    });
  }
  rows.sort((a, b) => b.hrs - a.hrs);

  // Add a synthetic Bank Holidays row — only count BH days each person did NOT work
  if (bhDateList.length > 0) {
    // Build a per-person set of dates they submitted project hours for
    const personWorkedDates = new Map<string, Set<string>>();
    for (const e of entries) {
      const key = e.personEmail.toLowerCase();
      if (!personWorkedDates.has(key)) personWorkedDates.set(key, new Set<string>());
      personWorkedDates.get(key)!.add(e.dateWorked as string);
    }

    const seenEmails = new Set<string>();
    let totalBHHours = 0;
    let bhCost = 0;
    for (const e of entries) {
      const key = e.personEmail.toLowerCase();
      if (seenEmails.has(key)) continue;
      seenEmails.add(key);
      const workedDates = personWorkedDates.get(key) ?? new Set<string>();
      const personBHHours = bhDateList
        .filter(([d]) => !workedDates.has(d))
        .reduce((s, [, h]) => s + h, 0);
      const person = peopleMap.get(key);
      const rate = costRateMap.get(person?.costType ?? '') ?? 0;
      totalBHHours += personBHHours;
      bhCost += personBHHours * rate;
    }

    if (totalBHHours > 0) {
      rows.push({
        code: 'BANK-HOL',
        projectName: 'Bank Holidays',
        type: ProjectType.Leave,
        owner: '—',
        hrs: totalBHHours,
        cost: Math.round(bhCost),
      });
    }
  }

  return rows;
}

function buildProjectPersonMap(
  entries: TimeEntry[],
  people: Person[],
  costTypes: CostTypeItem[],
): Map<string, ProjectPersonRow[]> {
  const peopleMap = new Map(people.map(p => [p.m365Email.toLowerCase(), p]));
  const costRateMap = new Map(costTypes.map(ct => [ct.name, ct.costRate]));

  const grouped = new Map<string, Map<string, { name: string; dept: string; hrs: number; cost: number }>>();
  for (const e of entries) {
    const projGroup = grouped.get(e.projectCode) ?? new Map();
    const key = e.personEmail.toLowerCase();
    const existing = projGroup.get(key);
    const person = peopleMap.get(key);
    const rate = costRateMap.get(person?.costType ?? '') ?? 0;
    if (existing) {
      existing.hrs += e.hours;
      existing.cost += e.hours * rate;
    } else {
      projGroup.set(key, {
        name: person?.fullName ?? e.personName ?? e.personEmail,
        dept: person?.department ?? '—',
        hrs: e.hours,
        cost: e.hours * rate,
      });
    }
    grouped.set(e.projectCode, projGroup);
  }

  const result = new Map<string, ProjectPersonRow[]>();
  for (const [code, personMap] of grouped) {
    const rows: ProjectPersonRow[] = Array.from(personMap.entries()).map(([id, v]) => ({
      personId: id,
      name: v.name,
      dept: v.dept,
      hrs: v.hrs,
      cost: Math.round(v.cost),
    }));
    rows.sort((a, b) => b.hrs - a.hrs);
    result.set(code, rows);
  }
  return result;
}

// ─── CSV export ───────────────────────────────────────────────────────────────

function downloadCSV(filename: string, rows: (string | number)[][]): void {
  const csv = rows
    .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
    .join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Excel export ─────────────────────────────────────────────────────────────

function downloadXLSX(filename: string, sheetName: string, rows: (string | number)[][]): void {
  const ws = XLSX.utils.aoa_to_sheet(rows);

  // Bold the header row
  const headerRange = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
    const cell = ws[XLSX.utils.encode_cell({ r: 0, c: col })];
    if (cell) cell.s = { font: { bold: true } };
  }

  // Bold + fill the grand-total row
  const totalRowIdx = rows.length - 1;
  for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
    const cell = ws[XLSX.utils.encode_cell({ r: totalRowIdx, c: col })];
    if (cell) cell.s = { font: { bold: true }, fill: { fgColor: { rgb: 'EEF2FF' }, patternType: 'solid' } };
  }

  // Auto column widths
  ws['!cols'] = rows[0].map((_, ci) => ({
    wch: Math.max(...rows.map(r => String(r[ci] ?? '').length), 10),
  }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, filename);
}

// ─── Component ────────────────────────────────────────────────────────────────

export function Reports() {
  const connector = useConnector();
  const [tab, setTab] = useState<ReportTab>('monthly');
  const [period, setPeriod] = useState<PeriodKey>('fy');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const [appliedRange, setAppliedRange] = useState<PeriodRange | null>(
    () => computePeriodRange('fy'),
  );
  const [expandedProject, setExpandedProject] = useState<string | null>(null);

  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [costTypes, setCostTypes] = useState<CostTypeItem[]>([]);
  const [bankHolidays, setBankHolidays] = useState<BankHoliday[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    if (!appliedRange) {
      setEntries([]);
      setBankHolidays([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setFetchError(null);

    const fromYear = parseInt(appliedRange.from.slice(0, 4), 10);
    const toYear = parseInt(appliedRange.to.slice(0, 4), 10);
    const bhYears = Array.from({ length: toYear - fromYear + 1 }, (_, i) => fromYear + i);

    Promise.all([
      connector.getTimeEntries({ dateFrom: appliedRange.from, dateTo: appliedRange.to }),
      connector.getPeople(),
      connector.getAllProjects(),
      connector.getCostTypes(),
      Promise.all(bhYears.map(y => connector.getBankHolidays(y))).then(arrs => arrs.flat()),
    ])
      .then(([entriesData, peopleData, projectsData, costTypesData, bhData]) => {
        if (cancelled) return;
        console.log('[Reports] BH fetched:', bhData.length, 'holidays. Period:', appliedRange.from, '→', appliedRange.to);
        setEntries(entriesData);
        setPeople(peopleData);
        setProjects(projectsData);
        setCostTypes(costTypesData);
        setBankHolidays(bhData);
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        setFetchError(err instanceof Error ? err.message : 'Failed to load report data');
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [connector, appliedRange]);

  const handlePeriodChange = (p: PeriodKey) => {
    setPeriod(p);
    if (p === 'custom') {
      setAppliedRange(
        customFrom && customTo ? computePeriodRange('custom', customFrom, customTo) : null,
      );
    } else {
      setAppliedRange(computePeriodRange(p));
    }
  };

  const handleApplyCustom = () => {
    const range = computePeriodRange('custom', customFrom, customTo);
    if (range) setAppliedRange(range);
  };

  // ─── Derived data ─────────────────────────────────────────────────────────

  // Deduplicate BH by date (SP can have UK + All rows for the same date), filtered to applied range
  const bhDatesInRange = useMemo(() => {
    if (!appliedRange) return new Map<string, number>();
    const bhByDate = new Map<string, number>();
    for (const bh of bankHolidays) {
      if (!bhByDate.has(bh.holidayDate)) bhByDate.set(bh.holidayDate, bh.defaultHours);
    }
    const result = new Map<string, number>();
    for (const [d, h] of bhByDate) {
      if (d >= appliedRange.from && d <= appliedRange.to) result.set(d, h);
    }
    console.log('[Reports] bhDatesInRange:', result.size, 'BH days in range:', Array.from(result.keys()));
    return result;
  }, [bankHolidays, appliedRange]);

  const personRows = useMemo(
    () => buildPersonRows(entries, people, projects, costTypes, bhDatesInRange),
    [entries, people, projects, costTypes, bhDatesInRange],
  );

  const projectRows = useMemo(
    () => buildProjectRows(entries, projects, people, costTypes, bhDatesInRange),
    [entries, projects, people, costTypes, bhDatesInRange],
  );

  const filteredPersonRows = useMemo(
    () => (tab === 'rd' ? personRows.filter(r => r.rd > 0) : personRows),
    [tab, personRows],
  );

  // Summary card totals always reflect all people (not filtered by R&D tab)
  const allPersonTotals = useMemo(
    () =>
      personRows.reduce(
        (a, r) => ({ rd: a.rd + r.rd, ga: a.ga + r.ga, leave: a.leave + r.leave, total: a.total + r.total }),
        { rd: 0, ga: 0, leave: 0, total: 0 },
      ),
    [personRows],
  );

  // Table footer totals respect the active tab filter
  const tableTotals = useMemo(
    () =>
      filteredPersonRows.reduce(
        (a, r) => ({ rd: a.rd + r.rd, ga: a.ga + r.ga, leave: a.leave + r.leave, total: a.total + r.total, cost: a.cost + r.cost }),
        { rd: 0, ga: 0, leave: 0, total: 0, cost: 0 },
      ),
    [filteredPersonRows],
  );

  const projectTotals = useMemo(
    () => projectRows.reduce((a, r) => ({ hrs: a.hrs + r.hrs, cost: a.cost + r.cost }), { hrs: 0, cost: 0 }),
    [projectRows],
  );

  const projectPersonMap = useMemo(
    () => buildProjectPersonMap(entries, people, costTypes),
    [entries, people, costTypes],
  );

  const { total: totalHrs, rd: rdHrs, ga: gaHrs, leave: leaveHrs } = allPersonTotals;

  const handleExport = (format: 'csv' | 'xlsx') => {
    const label = (appliedRange?.label ?? 'report').replace(/[^a-z0-9-]/gi, '_');
    const h = (n: number) => Math.round(n * 10) / 10;
    if (tab === 'project') {
      const header = ['Project Code', 'Project Name', 'Type', 'Owner', 'Hours', 'Est. Cost (£)'];
      const dataRows = projectRows.map(r => [r.code, r.projectName, r.type, r.owner, h(r.hrs), r.cost]);
      const footer = ['Grand Total', '', '', '', h(projectTotals.hrs), projectTotals.cost];
      const allRows = [header, ...dataRows, footer];
      if (format === 'xlsx') {
        const bdHeader = ['Project Code', 'Project Name', 'Person', 'Department', 'Hours', 'Est. Cost (£)'];
        const bdRows: (string | number)[][] = [];
        for (const r of projectRows) {
          if (r.code === 'BANK-HOL') {
            // Add one row per person — use only BH days they did NOT submit project hours for
            const bhDateList = Array.from(bhDatesInRange.entries());
            const personWorkedDatesExport = new Map<string, Set<string>>();
            for (const e of entries) {
              const key = e.personEmail.toLowerCase();
              if (!personWorkedDatesExport.has(key)) personWorkedDatesExport.set(key, new Set<string>());
              personWorkedDatesExport.get(key)!.add(e.dateWorked as string);
            }
            for (const pr of personRows) {
              if (pr.total <= 0) continue;
              const workedDates = personWorkedDatesExport.get(pr.personEmail) ?? new Set();
              const personBHHours = bhDateList
                .filter(([d]) => !workedDates.has(d))
                .reduce((s, [, hh]) => s + hh, 0);
              if (personBHHours <= 0) continue;
              const impliedRate = pr.cost / pr.total;
              const bhPersonCost = Math.round(personBHHours * impliedRate);
              bdRows.push(['BANK-HOL', 'Bank Holidays', pr.name, pr.dept, h(personBHHours), bhPersonCost]);
            }
          } else {
            const persons = projectPersonMap.get(r.code) ?? [];
            for (const p of persons) {
              bdRows.push([r.code, r.projectName, p.name, p.dept, h(p.hrs), p.cost]);
            }
          }
        }
        const ws1 = XLSX.utils.aoa_to_sheet(allRows);
        const ws2 = XLSX.utils.aoa_to_sheet([bdHeader, ...bdRows]);
        [ws1, ws2].forEach((ws, i) => {
          const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
          for (let c = range.s.c; c <= range.e.c; c++) {
            const cell = ws[XLSX.utils.encode_cell({ r: 0, c })];
            if (cell) cell.s = { font: { bold: true } };
          }
          const srcRows = i === 0 ? allRows : [bdHeader, ...bdRows];
          ws['!cols'] = Array.from({ length: range.e.c + 1 }, (_, ci) => ({
            wch: Math.max(...srcRows.map(row => String(row[ci] ?? '').length), 10),
          }));
        });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws1, 'Project Cost');
        XLSX.utils.book_append_sheet(wb, ws2, 'By Project By Person');
        XLSX.writeFile(wb, `evove-project-cost-${label}.xlsx`);
      } else {
        downloadCSV(`evove-project-cost-${label}.csv`, allRows);
      }
    } else {
      const header = ['Person', 'Department', 'R&D Hours', 'G&A Hours', 'Leave / BH', 'Total Hours', 'Est. Cost (£)'];
      const dataRows = filteredPersonRows.map(r => [r.name, r.dept, h(r.rd), h(r.ga), h(r.leave), h(r.total), r.cost]);
      const footer = ['Grand Total', '', h(tableTotals.rd), h(tableTotals.ga), h(tableTotals.leave), h(tableTotals.total), tableTotals.cost];
      const prefix = tab === 'rd' ? 'evove-rd-credit' : 'evove-monthly-summary';
      const sheetName = tab === 'rd' ? 'R&D Tax Credit' : 'Monthly Summary';
      const allRows = [header, ...dataRows, footer];
      if (format === 'xlsx') {
        downloadXLSX(`${prefix}-${label}.xlsx`, sheetName, allRows);
      } else {
        downloadCSV(`${prefix}-${label}.csv`, allRows);
      }
    }
  };

  const badgeClass = (type: string) =>
    (type as ProjectType) === ProjectType.RD ? 'badge-rd'
    : (type as ProjectType) === ProjectType.GA ? 'badge-ga'
    : (type as ProjectType) === ProjectType.Leave ? 'badge-leave'
    : (type as ProjectType) === ProjectType.Commercial ? 'badge-commercial'
    : 'badge-standard';

  const hasData = entries.length > 0;
  const showCustomPrompt = !loading && period === 'custom' && !appliedRange;
  const showEmpty = !loading && !!appliedRange && !hasData && !fetchError;

  return (
    <>
      {/* Topbar */}
      <div className="topbar">
        <div className="topbar-l">
          <div className="crumb">
            <span>Timesheets</span>
            <span className="sep">/</span>
            <span className="cur">Reports</span>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {(['monthly', 'rd', 'project'] as ReportTab[]).map(t => (
              <button
                key={t}
                className={`filter-tab${tab === t ? ' active' : ''}`}
                onClick={() => setTab(t)}
              >
                {t === 'monthly' ? 'Monthly Summary' : t === 'rd' ? 'R&D Tax Credit' : 'Project Cost'}
              </button>
            ))}
          </div>
        </div>
        <div className="topbar-r" style={{ display: 'flex', gap: 6 }}>
          <button
            className="btn btn-ghost"
            onClick={() => handleExport('xlsx')}
            disabled={loading || !hasData}
            title="Export to Excel (.xlsx)"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Export Excel
          </button>
          <button
            className="btn btn-ghost"
            onClick={() => handleExport('csv')}
            disabled={loading || !hasData}
            title="Export to CSV"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Export CSV
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="content">
        {/* Date filter bar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            marginBottom: 18,
            padding: '14px 16px',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 10,
          }}
        >
          <span
            style={{
              fontSize: 12.5,
              fontWeight: 600,
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            Period:
          </span>
          <div style={{ display: 'flex', gap: 4 }}>
            {(['month', 'fy', 'by', 'ytd', 'custom'] as PeriodKey[]).map(p => (
              <button
                key={p}
                className={`filter-tab${period === p ? ' active' : ''}`}
                onClick={() => handlePeriodChange(p)}
              >
                {p === 'month' ? 'This Month' : p === 'fy' ? 'FY Jul–Jun' : p === 'by' ? 'BY Jan–Dec' : p === 'ytd' ? 'YTD' : 'Custom'}
              </button>
            ))}
          </div>
          {period === 'custom' && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <DatePickerInput
                value={customFrom}
                onChange={setCustomFrom}
                style={{ height: 30, fontSize: 12.5 }}
              />
              <span style={{ color: 'var(--text-muted)' }}>to</span>
              <DatePickerInput
                value={customTo}
                onChange={setCustomTo}
                style={{ height: 30, fontSize: 12.5 }}
              />
              <button
                className="btn btn-ghost"
                style={{ height: 30, padding: '0 12px', fontSize: 12.5 }}
                onClick={handleApplyCustom}
                disabled={!customFrom || !customTo}
              >
                Apply
              </button>
            </div>
          )}
          <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)' }}>
            {appliedRange?.label ?? ''}
          </span>
        </div>

        {/* Error state */}
        {fetchError && (
          <div
            style={{
              padding: '12px 16px',
              background: '#fde8e8',
              border: '1px solid #f5c6c6',
              borderRadius: 8,
              marginBottom: 16,
              fontSize: 13,
              color: '#9b1c1c',
            }}
          >
            Failed to load report data: {fetchError}
          </div>
        )}

        {/* Summary stat cards */}
        {tab !== 'project' && (
          <div className="sum-grid" style={{ marginBottom: 18 }}>
            <div className="sum-card c-accent">
              <div className="sum-lbl">Total Hours</div>
              <div className="sum-val">{loading ? '\u2026' : fmtHours(totalHrs)}</div>
              <div className="sum-sub">across all staff</div>
            </div>
            <div className="sum-card">
              <div className="sum-lbl">R&amp;D Hours</div>
              <div className="sum-val">{loading ? '\u2026' : fmtHours(rdHrs)}</div>
              <div className="sum-sub">
                {loading || totalHrs === 0 ? '\u2014' : `${Math.round((rdHrs / totalHrs) * 100)}% of total`}
              </div>
            </div>
            <div className="sum-card">
              <div className="sum-lbl">G&amp;A Hours</div>
              <div className="sum-val">{loading ? '\u2026' : fmtHours(gaHrs)}</div>
              <div className="sum-sub">
                {loading || totalHrs === 0 ? '\u2014' : `${Math.round((gaHrs / totalHrs) * 100)}% of total`}
              </div>
            </div>
            <div className="sum-card">
              <div className="sum-lbl">Leave / BH</div>
              <div className="sum-val">{loading ? '\u2026' : fmtHours(leaveHrs)}</div>
              <div className="sum-sub">
                {loading || totalHrs === 0 ? '\u2014' : `${Math.round((leaveHrs / totalHrs) * 100)}% of total`}
              </div>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)', fontSize: 13 }}>
            Loading report data\u2026
          </div>
        )}

        {/* Custom range prompt */}
        {showCustomPrompt && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)', fontSize: 13 }}>
            Select a start and end date, then click Apply to generate the report.
          </div>
        )}

        {/* Empty state */}
        {showEmpty && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)', fontSize: 13 }}>
            No timesheet data found for the selected period.
          </div>
        )}

        {/* Report table */}
        {!loading && hasData && (
          <div style={{ overflowX: 'auto' }}>
            {tab === 'monthly' || tab === 'rd' ? (
              <table className="ts-tbl" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th className="tcol" style={{ textAlign: 'left' }}>Person</th>
                    <th style={{ textAlign: 'left' }}>Department</th>
                    <th style={{ textAlign: 'right' }}>R&amp;D Hours</th>
                    <th style={{ textAlign: 'right' }}>G&amp;A Hours</th>
                    <th style={{ textAlign: 'right' }}>Leave / BH</th>
                    <th style={{ textAlign: 'right' }}>Total Hours</th>
                    <th style={{ textAlign: 'right' }}>Est. Cost (£)</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPersonRows.map(r => (
                    <tr key={r.personId}>
                      <td className="tcell">
                        <div className="task-name">{r.name}</div>
                      </td>
                      <td style={{ textAlign: 'left', fontSize: 12, color: 'var(--text-muted)', padding: '9px 10px' }}>{r.dept}</td>
                      <td style={{ textAlign: 'right', fontSize: 13, fontFamily: '"DM Mono",monospace', color: 'var(--accent)', padding: '9px 10px' }}>
                        {fmtHours(r.rd)}
                      </td>
                      <td style={{ textAlign: 'right', fontSize: 13, fontFamily: '"DM Mono",monospace', padding: '9px 10px' }}>
                        {fmtHours(r.ga)}
                      </td>
                      <td style={{ textAlign: 'right', fontSize: 13, fontFamily: '"DM Mono",monospace', color: 'var(--text-muted)', padding: '9px 10px' }}>
                        {fmtHours(r.leave)}
                      </td>
                      <td style={{ textAlign: 'right', fontSize: 13, fontFamily: '"DM Mono",monospace', fontWeight: 600, padding: '9px 10px' }}>
                        {fmtHours(r.total)}
                      </td>
                      <td style={{ textAlign: 'right', fontSize: 13, fontFamily: '"DM Mono",monospace', color: 'var(--text-muted)', padding: '9px 10px' }}>
                        {r.cost > 0 ? `\u00a3${r.cost.toLocaleString()}` : '\u2014'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="tfoot-row">
                    <td className="tfoot-lbl">Grand Total</td>
                    <td />
                    <td style={{ textAlign: 'right', fontWeight: 700, fontSize: 13, fontFamily: '"DM Mono",monospace', color: 'var(--accent)' }}>
                      {fmtHours(tableTotals.rd)}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700, fontSize: 13, fontFamily: '"DM Mono",monospace' }}>{fmtHours(tableTotals.ga)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, fontSize: 13, fontFamily: '"DM Mono",monospace' }}>{fmtHours(tableTotals.leave)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, fontSize: 13, fontFamily: '"DM Mono",monospace' }}>{fmtHours(tableTotals.total)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, fontSize: 13, fontFamily: '"DM Mono",monospace' }}>
                      {tableTotals.cost > 0 ? `\u00a3${tableTotals.cost.toLocaleString()}` : '\u2014'}
                    </td>
                  </tr>
                </tfoot>
              </table>
            ) : (
              <table className="ts-tbl" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ width: 28 }} />
                    <th className="tcol" style={{ textAlign: 'left' }}>Project</th>
                    <th style={{ textAlign: 'center' }}>Type</th>
                    <th style={{ textAlign: 'center' }}>Owner</th>
                    <th style={{ textAlign: 'right' }}>Hours</th>
                    <th style={{ textAlign: 'right' }}>Est. Cost (£)</th>
                  </tr>
                </thead>
                <tbody>
                  {projectRows.map(r => {
                    const isExpanded = expandedProject === r.code;
                    const persons = projectPersonMap.get(r.code) ?? [];
                    return (
                      <Fragment key={r.code}>
                        <tr
                          style={{ cursor: 'pointer' }}
                          onClick={() => setExpandedProject(isExpanded ? null : r.code)}
                        >
                          <td style={{ textAlign: 'center', padding: '9px 6px', color: 'var(--text-muted)' }}>
                            <svg
                              viewBox="0 0 12 12"
                              width="11"
                              height="11"
                              fill="currentColor"
                              style={{ transition: 'transform 0.15s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block' }}
                            >
                              <path d="M4 2l5 4-5 4V2z" />
                            </svg>
                          </td>
                          <td className="tcell">
                            <div className="task-name">{r.code}</div>
                            {r.projectName && <div className="task-meta">{r.projectName}</div>}
                          </td>
                          <td style={{ textAlign: 'center', padding: '9px 10px' }}>
                            <span className={`badge ${badgeClass(r.type)}`}>{r.type}</span>
                          </td>
                          <td style={{ fontSize: 12, color: 'var(--text-muted)', padding: '9px 10px' }}>{r.owner}</td>
                          <td style={{ textAlign: 'right', fontSize: 13, fontFamily: '"DM Mono",monospace', fontWeight: 600, padding: '9px 10px' }}>
                            {fmtHours(r.hrs)}
                          </td>
                          <td style={{ textAlign: 'right', fontSize: 13, fontFamily: '"DM Mono",monospace', color: 'var(--text-muted)', padding: '9px 10px' }}>
                            {r.cost > 0 ? `\u00a3${r.cost.toLocaleString()}` : '\u2014'}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td />
                            <td colSpan={5} style={{ padding: '0 0 8px 0', background: 'var(--surface)' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                <thead>
                                  <tr style={{ background: 'var(--bg)' }}>
                                    <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>Person</th>
                                    <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>Department</th>
                                    <th style={{ textAlign: 'right', padding: '6px 10px', fontWeight: 600, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>Hours</th>
                                    <th style={{ textAlign: 'right', padding: '6px 10px', fontWeight: 600, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>Est. Cost (£)</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {persons.map(p => (
                                    <tr key={p.personId} style={{ borderBottom: '1px solid var(--border)' }}>
                                      <td style={{ textAlign: 'left', padding: '6px 10px' }}>{p.name}</td>
                                      <td style={{ textAlign: 'left', padding: '6px 10px', color: 'var(--text-muted)' }}>{p.dept}</td>
                                      <td style={{ textAlign: 'right', padding: '6px 10px', fontFamily: '"DM Mono",monospace', fontWeight: 600 }}>
                                        {fmtHours(p.hrs)}
                                      </td>
                                      <td style={{ textAlign: 'right', padding: '6px 10px', fontFamily: '"DM Mono",monospace', color: 'var(--text-muted)' }}>
                                        {p.cost > 0 ? `\u00a3${p.cost.toLocaleString()}` : '\u2014'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="tfoot-row">
                    <td />
                    <td className="tfoot-lbl">Grand Total</td>
                    <td />
                    <td />
                    <td style={{ textAlign: 'right', fontWeight: 700, fontSize: 13, fontFamily: '"DM Mono",monospace' }}>{fmtHours(projectTotals.hrs)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, fontSize: 13, fontFamily: '"DM Mono",monospace' }}>
                      {projectTotals.cost > 0 ? `\u00a3${projectTotals.cost.toLocaleString()}` : '\u2014'}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        )}

        {/* Privacy note */}
        <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--text-muted)' }}>
          🔒 Cost data (rates, totals £) visible to Sophie Franey / PMO Lead and System Administrators only.
        </div>
      </div>
    </>
  );
}
